import { IntroMedia, SmartMedia } from '../../src/components/Layout/Media';
import { Main, Page, PushShopping, Section } from '../../src/components/Layout';
import React, { PureComponent } from 'react';

import AudioGuide from '../../src/components/AudioGuide/AudioGuide';
import Breadcrumb from '../../src/components/Breadcrumb';
import { Container } from '../../src/components/Grid';
import EditoBlock from '../../src/components/PageBuilderBlocks/EditoBlock';
import FacebookEvent from '../../src/components/PageBuilderBlocks/FacebookEvent';
import Head from 'next/head';
import { Header } from '../../src/components/Header';
import ImageGallery from '../../src/components/ImageGallery/ImageGallery';
import { InfoBanner } from '../../src/components/Banner';
import InstagramFeed from '../../src/components/Feed/InstagramFeed';
import Picture from '../../src/components/Picture';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import Services from './Services';
import VideoGallery from '../../src/components/VideoGallery/VideoGallery';
import ZoomBlock from '../../src/components/PageBuilderBlocks/ZoomBlock';
import createContext from '../../src/utils/format/createContext';
import dynamic from 'next/dynamic';
import getPageBuilder from '../../src/apollo/graphql/page/getPageBuilder';
import { media } from '../../src/styles/media';
import { sizes } from '../../src/styles/media';
import styled from 'styled-components';
import { withI18N } from '../../src/hoc';

const RootSlider = styled.div`
  .slick-slide {
    padding: 0;
    flex: inherit;
  }

  .slick-slider {
    margin-bottom: 0;
  }

  .slick-track {
    overflow-x: hidden;
  }

  .slick-disabled {
    display: none;
  }

  .slick-arrow {
    z-index: 2;
  }

  .slick-next {
    right: 15px;

    ${media.tablet`
      right: 60px;
    `};
  }

  .slick-prev {
    left: 15px;

    ${media.tablet`
      left: 60px;
    `};
  }

  .slick-dots {
    position: absolute;
    bottom: 0;
    width: 100%;
    margin: ${props => props.theme.layout.gutter}px auto;

    li {
      position: relative;
      height: 2px;
      width: 12px;
      margin-right: 7px;
      border-radius: 0px;
      background-color: ${({ theme }) => theme.color.brand.lightGrey2};
      transition: all 0.3s ease;

      ${media.tablet`
        display: inline-block;
      `};
    }

    .slick-active {
      width: 42px;
      background-color: ${({ theme }) => theme.color.brand.white};
    }
  }
`;

const SliderImageWrapper = styled.div`
  position: relative;
  padding-top: 95%;

  ${media.tablet`
    padding-top: 0;
    max-height: 520px;
  `};

  img {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    object-fit: cover;
    font-family: 'object-fit: cover';
    height: 100%;
    width: 100%;

    ${media.tablet`
      position: relative;
      max-height: 520px;
    `};
  }
`;

const SliderComponentWithNoSSR = dynamic(
  import('../../src/components/Slider'),
  {
    // eslint-disable-next-line react/display-name
    loading: () => <div />,
    ssr: false
  }
);

class GenericPage extends PureComponent {
  static async getInitialProps({ query, i18n }) {
    return {
      slug: query && query.slug,
      i18n
    };
  }

  renderBlock(block, key) {
    switch (block.type) {
      case 'intro_block':
        return (
          <SmartMedia
            key={key}
            image={block.img_desktop}
            imageMobile={block.img_mobile}
            logo={block.logo_desktop}
            logoMobile={block.logo_mobile}
            content={block.detail.description}
            link={block.detail.cta_url}
            linkLabel={block.detail.cta_label}
          />
        );
      case 'content_block':
        return (
          <Section key={key}>
            <Container>
              <IntroMedia
                img={block.img_mobile}
                imgDesktop={block.img_desktop}
                reverse={block.img_position === 'right'}
                link={block.detail.cta_url}
                linkLabel={block.detail.cta_label}
                targetBlank={block.cta_target_blank}
              >
                {block.detail.content}
              </IntroMedia>
            </Container>
          </Section>
        );
      case 'push_block':
        return (
          <PushShopping
            key={key}
            title={block.detail.title}
            backgroundMobile={block.img_bg_mobile}
            background={block.img_bg_desktop}
            label={block.detail.cta_label}
            link={block.detail.cta_url}
          >
            <div
              dangerouslySetInnerHTML={{ __html: block.detail.description }}
            />
          </PushShopping>
        );
      case 'time_block':
        return (
          <InfoBanner
            key={key}
            place={block.place}
            type={block.place.type}
            hours={block.opening}
            content={block.detail.schedules}
            tel={block.phone}
            mail={block.detail.cta_url}
            ctaLabel={block.detail.cta_label}
          />
        );
      case 'slider_block':
        return (
          <RootSlider key={key}>
            <SliderComponentWithNoSSR dots={true} i18n={this.props.i18n}>
              {block.slides.map(slide => (
                <SliderImageWrapper key={slide.id}>
                  <Picture
                    src={slide.img_mobile}
                    pictures={[
                      { src: slide.img_desktop, minWidth: sizes.desktop }
                    ]}
                  />
                </SliderImageWrapper>
              ))}
            </SliderComponentWithNoSSR>
          </RootSlider>
        );
      case 'video_gallery_block':
        return (
          <Container key={key}>
            <VideoGallery data={block} />
          </Container>
        );
      case 'image_gallery_block':
        return (
          <Container key={key}>
            <ImageGallery data={block} />
          </Container>
        );
      case 'services_block':
        return (
          <Services key={key} data={block} lang={block.detail.language_code} />
        );
      case 'instagram_block':
        return <InstagramFeed key={key} title={block.detail.title} />;
      case 'audio_block':
        return (
          <Container key={key}>
            <AudioGuide data={block} lang={block.detail.language_code} />
          </Container>
        );
      case 'zoom_block':
        return (
          <Container key={key}>
            <ZoomBlock data={block} />
          </Container>
        );
      case 'edito_block':
        return (
          <Container key={key}>
            <EditoBlock data={block} />
          </Container>
        );
      case 'push_facebook_block':
        return (
          <Container key={key}>
            <FacebookEvent
              image={block.image}
              title={block.detail.title}
              label={block.detail.cta_label}
              url={block.detail.cta_url}
              date={block.detail.date}
              location={block.detail.location}
            />
          </Container>
        );
    }
  }

  renderPage(page) {
    const { t } = this.props;

    return (
      <div>
        <Head>
          <title key="title">Grand Hôtel-Dieu</title>
        </Head>
        <Header title={page.detail.title} image={page.img_desktop} />
        <Main>
          <Container>
            <Breadcrumb
              items={[
                { href: '/', label: t('common:nav.tabs.home') },
                { label: page.detail.title }
              ]}
            />
          </Container>
          {page.blocks.map((block, index) => this.renderBlock(block, index))}
        </Main>
      </div>
    );
  }

  render() {
    const { slug, i18n } = this.props;
    return (
      <Page>
        <Query
          query={getPageBuilder}
          context={createContext(i18n.language)}
          variables={{ slug: slug }}
        >
          {({ loading, error, data }) => {
            if (!loading && !error && data && data.getPageBuilder) {
              const page = data.getPageBuilder;
              return this.renderPage(page);
            }
            return null;
          }}
        </Query>
      </Page>
    );
  }
}

GenericPage.propTypes = {
  t: PropTypes.func,
  i18n: PropTypes.object,
  url: PropTypes.object
};

export default withI18N()(GenericPage);
